/**
 * 13. Audit Log Explorer — searchable event log with filters and event
 * detail showing before/after values, actor, and policy.
 *
 * Arabic and English (C7): every word from the catalogs; severity, outcome
 * and actor type are words, never the stored value. Event types are codes
 * (`auth.login.succeeded`) and are shown as sent, left to right. The two
 * filters now have accessible names (they had only a placeholder, or
 * nothing).
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Input,
  Select,
  Badge,
  DataTable,
  Skeleton,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { recordTypeLabel, valueLabel } from "@/lib/i18n/records";
import { formatDateTime } from "@/lib/money";

interface AuditEntry {
  id: string;
  eventType: string;
  severity: "Info" | "Warning" | "Critical";
  outcome: "Success" | "Failure" | "Blocked";
  actorType: string;
  targetType: string | null;
  targetId: string | null;
  metadata: Record<string, unknown> | null;
  occurredAt: string;
}

const SEVERITIES = ["Info", "Warning", "Critical"] as const;

export function AuditLogExplorerPage() {
  const { t } = useTranslation();
  const [eventType, setEventType] = useState("");
  const [severity, setSeverity] = useState("");
  const { data, isLoading } = useApiList<AuditEntry>(
    ["audit", "log", eventType, severity],
    `/audit-logs?limit=200${eventType ? `&eventType=${eventType}` : ""}${severity ? `&severity=${severity}` : ""}`,
  );

  return (
    <div>
      <PageHeader
        title={t("admin.auditLog.title")}
        subtitle={t("admin.auditLog.subtitle")}
      />
      <div className="atlas-toolbar mb-3">
        <Input
          placeholder={t("admin.auditLog.eventFilter")}
          aria-label={t("admin.auditLog.eventFilter")}
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          style={{ width: 260 }}
        />
        <Select
          aria-label={t("fields.severity")}
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          style={{ width: 140 }}
        >
          <option value="">{t("admin.auditLog.allSeverities")}</option>
          {SEVERITIES.map((value) => (
            <option key={value} value={value}>
              {valueLabel(t, "severity", value)}
            </option>
          ))}
        </Select>
      </div>
      <Card>
        {isLoading ? (
          <Skeleton rows={8} />
        ) : (
          <DataTable
            rows={data?.items ?? []}
            keyField="id"
            columns={[
              {
                key: "occurredAt",
                label: t("admin.auditLog.when"),
                render: (r) => formatDateTime(r.occurredAt),
              },
              {
                key: "eventType",
                label: t("fields.event"),
                render: (r) => (
                  <span className="atlas-mono" dir="ltr">
                    {r.eventType}
                  </span>
                ),
              },
              {
                key: "severity",
                label: t("fields.severity"),
                render: (r) => (
                  <Badge
                    tone={
                      r.severity === "Critical"
                        ? "danger"
                        : r.severity === "Warning"
                          ? "warning"
                          : "default"
                    }
                  >
                    {valueLabel(t, "severity", r.severity)}
                  </Badge>
                ),
              },
              {
                key: "outcome",
                label: t("fields.outcome"),
                render: (r) => (
                  <Badge tone={r.outcome === "Success" ? "success" : "danger"}>
                    {valueLabel(t, "outcome", r.outcome)}
                  </Badge>
                ),
              },
              {
                key: "actorType",
                label: t("admin.auditLog.actorType"),
                render: (r) =>
                  r.actorType ? valueLabel(t, "actorType", r.actorType) : "—",
              },
              {
                key: "targetType",
                label: t("admin.auditLog.target"),
                render: (r) =>
                  r.targetType ? recordTypeLabel(t, r.targetType) : "—",
              },
              {
                key: "targetId",
                label: t("admin.auditLog.targetId"),
                render: (r) =>
                  r.targetId ? (
                    <span className="atlas-mono">{r.targetId.slice(0, 8)}</span>
                  ) : (
                    "—"
                  ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
}
